import React from 'react';
import moment from 'moment';

// Backend karşılığı: contactDuplicateWatch.service.js findCrossEmailMatches
// (potential-customers/contacts/create ve quotes/erp-contacts/create uçları
// eşleşme bulunca 409 + bu satırları döner).
export interface CrossEmailMatch {
  id: number;
  full_name: string;
  email?: string;
  phone?: string;
  title?: string;
  created_at?: string;
  customer_key: string | number;
  customer_name: string;
  customer_type: string; // 'Potansiyel' | 'Cari'
  customer_owner?: string;
  added_by?: string;
  // 'email' = birebir aynı adres, 'domain' = aynı kurumsal uzantı (@firma.com)
  match_type?: 'email' | 'domain';
}

// 409 cross_email_conflict yanıtını yakalamak için ortak yardımcı:
// eşleşme varsa yanıt gövdesini ({ matches, message, ... }) döner, yoksa null.
export const getCrossEmailConflict = (err: any): { matches: CrossEmailMatch[]; message?: string } | null =>
  err?.response?.status === 409 && err?.response?.data?.cross_email_conflict ? err.response.data : null;

interface ContactEmailConflictModalProps {
  show: boolean;
  email: string;
  matches: CrossEmailMatch[];
  onCancel: () => void;
  onContinue: () => void;
  isSubmitting?: boolean;
  // Toplu akışta (Apollo "İletişim Bilgisi Topla") sıralı kuyruk bağlamı,
  // örn. "Apollo kişisi 2/3: Ali Veli" — Vazgeç yalnızca o kişiyi atlar.
  progressLabel?: string;
}

/**
 * Kişi ekleme sırasında aynı e-posta adresi (birebir) veya aynı kurumsal
 * e-posta uzantısı başka bir müşterinin ilgili kişilerinde kayıtlıysa açılan
 * uyarı modalı. Kimde olduğu ve kim tarafından eklendiği gösterilir;
 * "Yine de Devam Et" isteği force_cross_duplicate:true ile tekrarlar.
 */
const ContactEmailConflictModal: React.FC<ContactEmailConflictModalProps> = ({
  show,
  email,
  matches,
  onCancel,
  onContinue,
  isSubmitting,
  progressLabel,
}) => {
  if (!show) return null;
  // match_type gelmeyen eski yanıtlar birebir eşleşme sayılır
  const hasExact = matches.some((m) => m.match_type !== 'domain');
  const hasDomain = matches.some((m) => m.match_type === 'domain');
  const domain = email.includes('@') ? email.split('@')[1] : '';
  const title = hasExact ? 'Bu e-posta başka müşteride kayıtlı' : 'Aynı e-posta uzantısı başka müşteride kayıtlı';
  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1070 }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header border-bottom-0">
            <h5 className="modal-title fw-bold">{title}</h5>
            <button className="btn-close" onClick={onCancel} disabled={isSubmitting}></button>
          </div>
          <div className="modal-body pt-0">
            {progressLabel ? <div className="text-muted fw-semibold fs-8 mb-2">{progressLabel}</div> : null}
            <div className="alert alert-warning fs-7 mb-4">
              {hasExact && hasDomain ? (
                <>
                  <strong>{email}</strong> adresi ve <strong>@{domain}</strong> uzantılı adresler aşağıdaki müşteri
                  kayıtlarının ilgili kişilerinde mevcut.
                </>
              ) : hasExact ? (
                <>
                  <strong>{email}</strong> adresi aşağıdaki {matches.length} müşteri kaydının ilgili kişilerinde birebir
                  mevcut.
                </>
              ) : (
                <>
                  <strong>@{domain}</strong> uzantılı e-posta adresleri aşağıdaki {matches.length} müşteri kaydının
                  ilgili kişilerinde mevcut.
                </>
              )}{' '}
              Eklemeden önce kontrol edin; yine de eklemek istiyorsanız devam edebilirsiniz.
            </div>
            <div className="table-responsive">
              <table className="table table-row-bordered align-middle fs-7 gy-2 mb-0">
                <thead>
                  <tr className="fw-bold text-muted">
                    <th>Müşteri</th>
                    <th>Tür</th>
                    <th>Müşterinin Satışçısı</th>
                    <th>Kayıtlı Kişi</th>
                    <th>Eşleşme</th>
                    <th>Ekleyen</th>
                    <th>Eklenme</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((m) => (
                    <tr key={`${m.customer_type}-${m.id}`}>
                      <td className="fw-semibold text-gray-800">{m.customer_name}</td>
                      <td>
                        <span className={`badge badge-light-${m.customer_type === 'Cari' ? 'success' : 'primary'}`}>
                          {m.customer_type}
                        </span>
                      </td>
                      <td>{m.customer_owner || '-'}</td>
                      <td>
                        {m.full_name}
                        {m.title ? <span className="text-muted"> — {m.title}</span> : null}
                        {m.email ? <div className="text-muted fs-8">{m.email}</div> : null}
                      </td>
                      <td>
                        <span className={`badge badge-light-${m.match_type === 'domain' ? 'info' : 'danger'}`}>
                          {m.match_type === 'domain' ? 'Aynı uzantı' : 'Birebir e-posta'}
                        </span>
                      </td>
                      <td>{m.added_by || '-'}</td>
                      <td className="text-nowrap">{m.created_at ? moment(m.created_at).format('DD.MM.YYYY') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="modal-footer border-top-0 pt-0">
            <button type="button" className="btn btn-light" onClick={onCancel} disabled={isSubmitting}>
              Vazgeç
            </button>
            <button type="button" className="btn btn-warning" onClick={onContinue} disabled={isSubmitting}>
              {isSubmitting ? <span className="spinner-border spinner-border-sm me-2"></span> : null}
              Yine de Devam Et
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactEmailConflictModal;
